import { getLanguage, t } from 'react-switch-lang';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { faDownload, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'react-data-grid/lib/styles.css';
import DataGrid from 'react-data-grid';
import style from '../../styles/Reports.module.scss';
import ControlPanel from './ControlPanel';
import { APP_LANG_KEYS, DIRECTION, DYNAMIC_FIELDS, PAYMENT_OPTIONS_LANG_KEYS, REPORTS } from '../../utils/Constants';
import { convertUTCToTimezoneDateTimeString, filterAmount, formatReportAmount, getAppDisplayText, getColumnWidth, getDefaultDateRange, getPaymentOptionDisplayText, getReportWidthStyle, inputStopPropagation, isLabelAvailable, onSortColumnsChange, saveReportAsCSV, selectStopPropagation } from '../../utils/ReportsHelpers';
import useReportData from '../../hooks/useReportData';
import useSearchCooldown from '../../hooks/useSearchCooldown';
import ErrorAlert from '../alerts/ErrorAlert';
import LoadingSpinner from '../LoadingSpinner';
import { events, logAmpEvent } from '../../utils/Amplitude';
import { setShowReportTooltips } from '../../redux/actions/ReportActions';
import { setScreenReaderAlert } from '../../utils/Accessibility';
import ColumnHeaderRenderer from './ColumnHeaderRenderer';
import Button from '../Button';

const COLUMN_WIDTHS = {
  fr: {
    PaidOn: 160,
    Amount: 100,
    Fee: 100,
    ClientRef: 200,
    InvoiceNo: 200,
    TransactionNumber: 200,
    PayerName: 270,
    PayerEmail: 400, // big enough to support max email size
    PaymentOption: 180,
    App: 180,
    PartnerField01: 200,
    PartnerField02: 200,
    PartnerField03: 200,
    PartnerField04: 200,
    PartnerField05: 200,
  },
  en: {
    PaidOn: 160,
    Amount: 100,
    Fee: 100,
    ClientRef: 200,
    InvoiceNo: 200,
    TransactionNumber: 200,
    PayerName: 270,
    PayerEmail: 400, // big enough to support max email size
    PaymentOption: 180,
    App: 180,
    PartnerField01: 200,
    PartnerField02: 200,
    PartnerField03: 200,
    PartnerField04: 200,
    PartnerField05: 200,
  },
};

function PaymentsReport() {
  const defaultDateRange = getDefaultDateRange(REPORTS.PAYMENTS);

  const [data, loading, longLoadTime, error, getReportData] = useReportData(REPORTS.PAYMENTS);
  const dispatch = useDispatch();
  const lang = getLanguage();
  const defaultFilters = {
    PaidOn: '',
    Amount: '',
    Fee: '',
    ClientRef: '',
    InvoiceNo: '',
    TransactionNumber: '',
    PayerName: '',
    PayerEmail: '',
    PaymentOption: '',
    App: '',
    PartnerField01: '',
    PartnerField02: '',
    PartnerField03: '',
    PartnerField04: '',
    PartnerField05: '',
  };
  const [filters, setFilters] = useState(defaultFilters);

  const storedStartDate = useSelector((state) => state.reports?.paymentsReport?.startDate);
  const storedEndDate = useSelector((state) => state.reports?.paymentsReport?.endDate);
  const currentPartner = useSelector((state) => state.partner.currentPartner);
  const customFieldLabels = currentPartner?.CustomAccountLabels;
  const startDate = storedStartDate || defaultDateRange.startDate;
  const endDate = storedEndDate || defaultDateRange.endDate;
  const timezone = useSelector((state) => state.reports.paymentsReport?.timezone);

  const { searchOnCooldown, startSearchCooldown } = useSearchCooldown();

  const [selectedCell, setSelectedCell] = useState(null);
  const [selectedRowID, setSelectedRowID] = useState(null);

  const [sortColumns, setSortColumns] = useState([
    {
      columnKey: 'PaidOn',
      direction: DIRECTION.DESCENDING,
    },
  ]);

  function handleSearch(search) {
    if (loading || searchOnCooldown(search)) return undefined;
    startSearchCooldown(search);

    setSelectedCell(null);
    setSortColumns([
      {
        columnKey: 'PaidOn',
        direction: DIRECTION.DESCENDING,
      },
    ]);

    setFilters(defaultFilters);

    // Reset highlight and refund form
    setSelectedRowID(null);

    return getReportData(search);
  }

  const dynamicColumns = useMemo(() => {
    const dynamicCols = {};
    const getColumnHeaderLabel = (labelObj) => (lang === 'fr' ? labelObj?.Fr : labelObj?.En);
    // eslint-disable-next-line no-unused-vars
    Object.entries(DYNAMIC_FIELDS).forEach(([_, name]) => {
      const customFieldLabelObj = customFieldLabels[name];
      // check is the field labels exist in the PSWebConfig.
      if (isLabelAvailable(customFieldLabelObj)) {
        dynamicCols[name] = {
          key: name,
          name: getColumnHeaderLabel(customFieldLabelObj),
          renderSummaryCell: ({ tabIndex }) => (
            <div className={style.filterCell}>
              <input
                tabIndex={tabIndex}
                type="text"
                onChange={(e) => setFilters((f) => ({
                  ...f,
                  [name]: e.target.value,
                }))}
                onKeyDown={inputStopPropagation}
                onBlur={(e) => {
                  if (e.target.value.length > 0) {
                    logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: name });
                  }
                }}
              />
            </div>
          ),
        };
      }
      return null;
    });
    return dynamicCols;
  }, [customFieldLabels, lang]);

  const columns = useMemo(() => [
    {
      key: 'PaidOn',
      name: t('Reports_PaymentsReportCol_CreatedDate'),
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <input
            tabIndex={tabIndex}
            type="text"
            onChange={(e) => setFilters((f) => ({
              ...f,
              PaidOn: e.target.value,
            }))}
            onKeyDown={inputStopPropagation}
            onBlur={(e) => {
              if (e.target.value.length > 0) logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'PaidOn' });
            }}
          />
        </div>
      ),
      renderCell: ({ row }) => convertUTCToTimezoneDateTimeString(row.PaidOn, timezone),
    },
    {
      key: 'Amount',
      name: t('Reports_PaymentsReportCol_Amount'),
      cellClass: style.amountCell,
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <input
            tabIndex={tabIndex}
            type="text"
            onChange={(e) => setFilters((f) => ({
              ...f,
              Amount: e.target.value,
            }))}
            onKeyDown={inputStopPropagation}
            onBlur={(e) => {
              if (e.target.value.length > 0) logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'Amount' });
            }}
          />
        </div>
      ),
      renderCell: ({ row }) => formatReportAmount(row.Amount),
    },
    {
      key: 'Fee',
      name: t('Reports_PaymentsReportCol_Fee'),
      cellClass: style.amountCell,
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <input
            tabIndex={tabIndex}
            type="text"
            onChange={(e) => setFilters((f) => ({
              ...f,
              Fee: e.target.value,
            }))}
            onKeyDown={inputStopPropagation}
            onBlur={(e) => {
              if (e.target.value.length > 0) logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'Fee' });
            }}
          />
        </div>
      ),
      renderCell: ({ row }) => formatReportAmount(row.Fee),
    },
    ...(dynamicColumns.ClientRef ? [dynamicColumns.ClientRef] : []),
    ...(dynamicColumns.InvoiceNo ? [dynamicColumns.InvoiceNo] : []),
    {
      key: 'TransactionNumber',
      name: t('Reports_PaymentsReportCol_TransactionNumber'),
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <input
            tabIndex={tabIndex}
            type="text"
            onChange={(e) => setFilters((f) => ({
              ...f,
              TransactionNumber: e.target.value,
            }))}
            onKeyDown={inputStopPropagation}
            onBlur={(e) => {
              if (e.target.value.length > 0) logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'TransactionNumber' });
            }}
          />
        </div>
      ),
    },
    ...(dynamicColumns.PayerName ? [dynamicColumns.PayerName] : []),
    {
      key: 'PayerEmail',
      name: t('Reports_PaymentsReportCol_PayerEmail'),
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <input
            tabIndex={tabIndex}
            type="text"
            onChange={(e) => setFilters((f) => ({
              ...f,
              PayerEmail: e.target.value,
            }))}
            onKeyDown={inputStopPropagation}
            onBlur={(e) => {
              if (e.target.value.length > 0) logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'PayerEmail' });
            }}
          />
        </div>
      ),
    },
    {
      key: 'PaymentOption',
      name: t('Reports_PaymentsReportCol_PaymentOption'),
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <select
            tabIndex={tabIndex}
            onChange={(e) => {
              logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'Status' });
              setFilters((f) => ({
                ...f,
                PaymentOption: e.target.value,
              }));
            }}
            onKeyDown={selectStopPropagation}
          >
            <option value="">{t('Reports_Payments_Option_Dropdown_All')}</option>
            <option value={t(PAYMENT_OPTIONS_LANG_KEYS.M)}>{t(PAYMENT_OPTIONS_LANG_KEYS.M)}</option>
            <option value={t(PAYMENT_OPTIONS_LANG_KEYS.J)}>{t(PAYMENT_OPTIONS_LANG_KEYS.J)}</option>
            <option value={t(PAYMENT_OPTIONS_LANG_KEYS.T)}>{t(PAYMENT_OPTIONS_LANG_KEYS.T)}</option>
            <option value={t(PAYMENT_OPTIONS_LANG_KEYS.A)}>{t(PAYMENT_OPTIONS_LANG_KEYS.A)}</option>
            <option value={t(PAYMENT_OPTIONS_LANG_KEYS.D)}>{t(PAYMENT_OPTIONS_LANG_KEYS.D)}</option>
            <option value={t(PAYMENT_OPTIONS_LANG_KEYS.P)}>{t(PAYMENT_OPTIONS_LANG_KEYS.P)}</option>
          </select>
        </div>
      ),
      renderCell: ({ row }) => getPaymentOptionDisplayText(row.PaymentOption),
    },
    {
      key: 'App',
      name: t('Reports_PaymentsReportCol_App'),
      renderSummaryCell: ({ tabIndex }) => (
        <div className={style.filterCell}>
          <select
            tabIndex={tabIndex}
            onChange={(e) => {
              logAmpEvent(events.USER_FILTERED_REPORT_COLUMN, { Column: 'Status' });
              setFilters((f) => ({
                ...f,
                App: e.target.value,
              }));
            }}
            onKeyDown={selectStopPropagation}
          >
            <option value="">{t('Reports_Payments_Option_Dropdown_All')}</option>
            <option value={t(APP_LANG_KEYS.PaySimplyMobile)}>
              {t(APP_LANG_KEYS.PaySimplyMobile)}
            </option>
            <option value={t(APP_LANG_KEYS.PaySimply)}>{t(APP_LANG_KEYS.PaySimply)}</option>
          </select>
        </div>
      ),
      renderCell: ({ row }) => getAppDisplayText(row.App),
    },
    ...(dynamicColumns.PartnerField01 ? [dynamicColumns.PartnerField01] : []),
    ...(dynamicColumns.PartnerField02 ? [dynamicColumns.PartnerField02] : []),
    ...(dynamicColumns.PartnerField03 ? [dynamicColumns.PartnerField03] : []),
    ...(dynamicColumns.PartnerField04 ? [dynamicColumns.PartnerField04] : []),
    ...(dynamicColumns.PartnerField05 ? [dynamicColumns.PartnerField05] : []),
  ].map((col, idx, cols) => {
    const column = { ...col };
    const numberOfColumns = cols.length;
    // Set default column options here
    column.headerCellClass = style.columnHeader;
    if (idx === numberOfColumns - 1) {
      // Min width is set for the last column to prevent the user from collapsing the column too far
      column.minWidth = getColumnWidth(COLUMN_WIDTHS, column.key, lang);
    } else {
      column.width = getColumnWidth(COLUMN_WIDTHS, column.key, lang);
    }
    if (!column.renderHeaderCell) {
      column.renderHeaderCell = ({ sortDirection }) => (
        <ColumnHeaderRenderer
          column={column}
          sortDirection={sortDirection}
          tooltip={column.tooltip}
          isSelected={selectedCell?.column === idx && selectedCell?.row === -2}
        />
      );
    }
    return column;
  }), [dynamicColumns, timezone, lang, selectedCell?.column, selectedCell?.row]); // eslint-disable-line max-len

  const getColumnHeaders = () => {
    const headers = columns.map((col) => col.name.trim());
    headers.push(t('Reports_ExportCSV_Sequence'));
    return headers;
  };

  const rows = useMemo(() => data?.map((p) => ({
    id: p.Sequence,
    PaidOn: p.Date,
    Amount: p.Amount,
    Fee: p.Fee,
    ClientRef: p.ClientRef,
    InvoiceNo: p.InvoiceNo,
    TransactionNumber: p.TransNo,
    PayerName: p.PayerName,
    PayerEmail: p.Email,
    PaymentOption: p.PmtType,
    App: p.App,
    PartnerField01: p.PartnerField01,
    PartnerField02: p.PartnerField02,
    PartnerField03: p.PartnerField03,
    PartnerField04: p.PartnerField04,
    PartnerField05: p.PartnerField05,
  })), [data]);

  const filteredAndSortedRows = useMemo(() => {
    const filteredRows = rows?.filter((row) => (
      (filters.PaidOn ?
        convertUTCToTimezoneDateTimeString(row.PaidOn, timezone).replace(/[ \-:]/g, '').startsWith(filters.PaidOn.replace(/[ \-:]/g, '')) : true) &&

      (filters.Amount ? filterAmount(formatReportAmount(row.Amount), filters.Amount) : true) &&

      (filters.Fee ? filterAmount(formatReportAmount(row.Fee), filters.Fee) : true) &&

      (filters.ClientRef ?
        row.ClientRef.toLowerCase().startsWith(filters.ClientRef.toLowerCase()) : true) &&

      (filters.InvoiceNo ?
        row.InvoiceNo.toLowerCase().startsWith(filters.InvoiceNo.toLowerCase()) : true) &&

      (filters.TransactionNumber ?
        row.TransactionNumber.toLowerCase()
          .startsWith(filters.TransactionNumber.toLowerCase()) : true) &&

      (filters.PayerName ?
        row.PayerName.toLowerCase().startsWith(filters.PayerName.toLowerCase()) : true) &&

      (filters.PayerEmail ?
        row.PayerEmail.toLowerCase().startsWith(filters.PayerEmail.toLowerCase()) : true) &&

      (filters.PaymentOption ?
        getPaymentOptionDisplayText(row.PaymentOption)
          .toLowerCase().startsWith(filters.PaymentOption.toLowerCase()) : true) &&

      (filters.App ?
        getAppDisplayText(row.App).toLowerCase().startsWith(filters.App.toLowerCase()) : true) &&

      (filters.PartnerField01 ?
        row.PartnerField01.toLowerCase().startsWith(filters.PartnerField01.toLowerCase()) : true) &&

      (filters.PartnerField02 ?
        row.PartnerField02.toLowerCase().startsWith(filters.PartnerField02.toLowerCase()) : true) &&

      (filters.PartnerField03 ?
        row.PartnerField03.toLowerCase().startsWith(filters.PartnerField03.toLowerCase()) : true) &&

      (filters.PartnerField04 ?
        row.PartnerField04.toLowerCase().startsWith(filters.PartnerField04.toLowerCase()) : true) &&

      (filters.PartnerField05 ?
        row.PartnerField05.toLowerCase().startsWith(filters.PartnerField05.toLowerCase()) : true)
    ));

    if (rows?.length > 0 && filteredRows?.length === 0) {
      return filteredRows;
    }
    if (sortColumns.length === 0 || !rows) return filteredRows;

    const { columnKey, direction } = sortColumns[0];

    let sortedRows = [...filteredRows];

    switch (columnKey) {
      case 'PaidOn':
      case 'ClientRef':
      case 'InvoiceNo':
      case 'TransactionNumber':
      case 'PayerName':
      case 'PayerEmail':
      case 'PaymentOption':
      case 'App':
        sortedRows = sortedRows.sort((a, b) => {
          if (!a[columnKey]) return -1;
          return a[columnKey].localeCompare(b[columnKey]);
        });
        break;
      case 'Amount':
      case 'Fee': sortedRows = sortedRows.sort((a, b) => {
        if (!a[columnKey]) return -1;
        return lang === 'en' ?
          Number.parseFloat(a[columnKey].replace(/[,]/g, '')) - Number.parseFloat(b[columnKey].replace(/[,]/g, '')) :
          Number.parseFloat(a[columnKey].replace(/[\s]/g, '').replace(/[,]/g, '.')) - Number.parseFloat(b[columnKey].replace(/[\s]/g, '').replace(/[,]/g, '.'));
      });
        break;
      default:
    }

    return direction === DIRECTION.DESCENDING ? sortedRows.reverse() : sortedRows;
  }, [rows, sortColumns, filters, lang, timezone]);

  useEffect(() => {
    if (rows?.length > 0 && filteredAndSortedRows?.length === 0) setScreenReaderAlert(t('Reports_NoFilteredResults_ScreenReaderAlert'));
  }, [rows, filteredAndSortedRows]);

  useEffect(() => {
    const deselectCell = () => {
      setSelectedCell(null);
    };
    // this is done to prevent tooltips from becoming misaligned on scroll
    window.addEventListener('scroll', deselectCell);
    return () => {
      window.removeEventListener('scroll', deselectCell);
    };
  }, []);

  return (
    <div className={style.reportContentContainer}>
      <p>{t('Reports_PaymentsReport_Description')}</p>
      <ControlPanel
        onSubmit={(search) => handleSearch(search)}
        loading={loading}
        searchOnCooldown={searchOnCooldown}
        report={REPORTS.PAYMENTS}
        startDate={startDate}
        endDate={endDate}
        timezone={null}
        includeTimeValues
        includeTimezone
        labelStart={t('Reports_DateTimeSelectorPayments_Start_Title')}
        labelEnd={t('Reports_DateTimeSelectorPayments_End_Title')}
      />
      {error && (
        <div className={style.gridMessageContainer}>
          <ErrorAlert msg={error} />
        </div>
      )}

      {!error && loading && (
        <div className={style.gridLoadMessageContainer}>
          <LoadingSpinner />
          {longLoadTime && <span>{t('Reports_LongLoadTime_Title')}</span>}
        </div>
      )}

      {!error && !loading && rows && (rows.length === 0 ? (
        <div className={style.gridMessageContainer}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <span className={style.gridMessage}>{t('Reports_NoRecordsFound_Title')}</span>
        </div>
      ) : (
        <div style={{ width: getReportWidthStyle(COLUMN_WIDTHS, lang) }}>
          <div className={style.exportBtnContainer}>
            <Button
              type="button"
              ariaLabel={t('Reports_Payments_ExportCSV_ButtonDescription', { startDate, endDate })}
              linkBtn
              ampEvent={events.USER_CLICKED_REPORT_EXPORT_CSV}
              ampEventProps={{ 'Report Type': REPORTS.PAYMENTS }}
              onClick={() => {
                saveReportAsCSV(
                  filteredAndSortedRows,
                  getColumnHeaders(),
                  currentPartner,
                  startDate,
                  endDate,
                  timezone
                );
              }}
            >
              <FontAwesomeIcon icon={faDownload} />
              <span>{t('Reports_ExportCSV_Button')}</span>
            </Button>
          </div>
          <DataGrid
            style={{ '--rows': rows.length }}
            className={`${rows.length === 0 ? style.gridEmpty : style.grid} rdg-light`} // rdg-light is necessary to enforce light-theme
            columns={columns}
            rows={filteredAndSortedRows}
            topSummaryRows={[{}]} // empty object array needed to enable summary rows
            rowKeyGetter={(row) => row.id}
            rowHeight={35}
            rowClass={(row) => {
              // Check if the row is selected
              if (selectedRowID !== null && row?.id === selectedRowID) {
                return `${style.row} ${style.highlightedRow}`; // Add highlight class
              }
              // Return default class for other rows
              return style.row;
            }}
            sortColumns={sortColumns}
            onSortColumnsChange={(cols) => onSortColumnsChange(cols, setSortColumns)}
            defaultColumnOptions={{
              sortable: true,
              resizable: true,
            }}
            onScroll={() => {
              dispatch(setShowReportTooltips(false));
              setSelectedCell(null);
            }}
            onSelectedCellChange={
              ({ rowIdx, row, column }) => {
                setSelectedCell({ row: rowIdx, column: column.idx });
                if (rowIdx > -1) { // Ignore header and filter row
                  setSelectedRowID(row.id); // Highlight row
                }
              }
            }
          />
          <div className={style.gridFooter} />
        </div>
      ))}
    </div>
  );
}

export default PaymentsReport;
